import React, { useEffect, useRef, useState } from 'react';
import ButtonAccount from '../../../components/ButtonAccount';
import { countryOptions, langOptions } from '../../../mock';
import { Controller, useForm } from 'react-hook-form';
import Input from '../../../components/Input';
import SelectInput from '../../../components/SelectInput';
import Dialog from '../../../components/Dialog';
import ButtonAuth from '../../../components/ButtonAuth';
import OtpInput from 'react-otp-input';
import rocketBoyImg from '../../../assets/images/barbfullLogo.png';
import { useTrackingContext } from '../../../context/trackingContext';
import { useUserContext } from '../../../context/userContext';
import useClient from '../../../hooks/useClient';
import config from '../../../config';
import Alert from '../../../components/Alert';
import {
  startsAndEndsWithAlphaNumeric,
  containsValidUsernameCharacters
} from '../../../utils/validateUsername';

const AccountInfo = ({ handleTabChange }) => {
  const { trackPageView, trackFormStart, trackFormSubmit, trackClick, setEmail, trackError } =
    useTrackingContext();
  const { userData, fetchUser, setUserData } = useUserContext();
  const client = useClient();

  const { firstName, username, emailAddress, country, language, parentalEmail, discordName } =
    userData || {};

  useEffect(() => {
    trackPageView({
      pageName: 'Account Info tab'
    });
  }, []);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty }
  } = useForm({ mode: 'onTouched' });

  const {
    control: controlEmail,
    reset: resetEmail,
    formState: { isValid: isValidEmail, isDirty: isDirtyEmail },
    getValues,
    register
  } = useForm({ mode: 'onTouched' });

  const {
    handleSubmit: handleSubmitVerify,
    formState: { isValid: isValidVerify, isDirty: isDirtyVerify, isSubmitting: isSubmittingVerify }
  } = useForm({ mode: 'onTouched' });

  const [isEdit, setIsEdit] = useState(false);
  const [isEditEmail, setIsEditEmail] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const [otp, setOtp] = useState('');

  useEffect(() => {
    isDirty &&
      trackFormStart({
        formName: 'Edit Account Details Started'
      });
  }, [isDirty]);

  useEffect(() => {
    isDirtyVerify &&
      trackFormStart({
        formName: 'Verification Code - Request Data - started'
      });
  }, [isDirtyVerify]);

  useEffect(() => {
    isDirtyEmail &&
      trackFormStart({
        formName: 'Edit Account Details Started'
      });
  }, [isDirtyEmail]);

  const onSubmit = async (data) => {
    trackFormSubmit({
      formName: 'Edit Account details form submit'
    });

    try {
      const res = await client.put(
        `${config.everywhereApi}/users/self/profile/update-profile-data`,
        {
          username: data.username,
          firstName: data.firstName,
          language: data.language?.value,
          discordName: data.discordName,
          parentalEmail: data.parentalEmail
        }
      );

      setInternalAlert({ text: res.data.data.message, success: true });
    } catch (err) {
      const error = err?.response?.data?.error;
      trackError({ error_text: 'error editing account details' });

      setInternalAlert({
        text: error ? error?.errorMessage + ' - ' + error.errorCode : 'Unknown error',
        success: false
      });

      setUserData();
      fetchUser();
      reset();
    }
  };

  const onVerificationSubmit = async (type, email) => {
    trackFormSubmit({
      formName: 'verify Request Data code form submit'
    });

    try {
      if (type === 'gdpr') {
        await client.put(`${config.everywhereApi}/users/self/profile/verify-gdpr`, {
          emailCode: otp
        });

        setOtp('');
        handleTabChange(6);
        setVerifying(null);
      } else if (type === 'delete') {
        const res = await client.put(`${config.everywhereApi}/users/self/profile/verify-delete`, {
          emailCode: otp
        });

        setDeleteAlert({ text: res.data.data.message, success: true });
        setOtp('');
        setVerifying(null);
        fetchUser();
      } else {
        const { email, updateMarketing } = getValues();

        const res = await client.put(`${config.everywhereApi}/users/self/profile/update-email`, {
          emailCode: otp,
          updateMarketing,
          emailAddress: email
        });

        setEmailAlert({ text: res.data.data.message, success: true });
        setOtp('');
        setVerifying(null);
        fetchUser();
      }
    } catch (err) {
      trackError({
        error_text:
          type === 'gdpr'
            ? 'incorrect code entered gdpr'
            : type === 'delete'
            ? 'incorrect code entered delete'
            : 'incorrect email change code'
      });

      setAlert('failure');
    }
  };

  const getNewCode = async (type) => {
    try {
      trackClick({ btnName: 'send new verification code - request data' });

      const res = await client.put(`${config.everywhereApi}/users/self/profile/generate-code`, {
        type
      });

      setEmail(res.data.data.email);

      setAlert('newCode');
    } catch {
      trackError({ error_text: 'failed to send new code' });
      setAlert('noNewCode');
    }
  };

  const getNewEmailCode = async (type) => {
    try {
      trackClick({ btnName: 'send new verification code - change email' });

      const { email } = getValues();

      const res = await client.put(
        `${config.everywhereApi}/users/self/profile/generate-new-email-code`,
        {
          type,
          email
        }
      );

      setEmail(res.data.data.email);

      setAlert('newCode');
    } catch {
      trackError({ error_text: 'failed to send new code' });
      setAlert('noNewCode');
    }
  };

  const renderButtons = () => {
    return isEdit ? (
      <div className="flex">
        <ButtonAccount disabled={!isValid || !isDirty} label="Save" type="submit" />
        <ButtonAccount type="reset" label="Cancel" />
      </div>
    ) : (
      <ButtonAccount
        label="Edit"
        onClick={() => {
          trackClick({ btnName: 'Edit account details open' });
          setIsEdit(true);
        }}
      />
    );
  };

  const openVerificationDialog = async (type) => {
    setVerifying(type);

    try {
      await client.put(`${config.everywhereApi}/users/self/profile/generate-code`, {
        type
      });
    } catch {
      setAlert('noNewCode');
    }
  };

  const openEmailVerificationDialog = async (type) => {
    const { email } = getValues();

    try {
      await client.put(`${config.everywhereApi}/users/self/profile/generate-new-email-code`, {
        type,
        email
      });

      setVerifying(type);
    } catch (err) {
      const error = err?.response?.data?.error;

      setEmailAlert({
        text: error ? error?.errorMessage + ' - ' + error.errorCode : 'Unknown error',
        success: false
      });
      resetEmail();
    }
  };

  const removeDelete = async () => {
    try {
      const res = await client.delete(
        `${config.everywhereApi}/users/self/profile/remove-delete-request`
      );

      setDeleteAlert({ text: res.data.data.message, success: true });
      fetchUser();
    } catch (err) {
      const error = err?.response?.data?.error;
      setDeleteAlert({
        text: error ? error?.errorMessage + ' - ' + error.errorCode : 'Unknown error',
        success: false
      });
    }
  };

  const renderEmailButtons = () => {
    return isEditEmail ? (
      <div className="flex">
        <ButtonAccount disabled={!isValidEmail || !isDirtyEmail} label="Save" type="submit" />
        <ButtonAccount type="reset" label="Cancel" />
      </div>
    ) : (
      <ButtonAccount
        label="Edit"
        onClick={() => {
          trackClick({ btnName: 'Edit email address open' });
          setIsEditEmail(true);
        }}
      />
    );
  };

  const [alert, setAlert] = useState(false);
  const [internalAlert, setInternalAlert] = useState(null);
  const [deleteAlert, setDeleteAlert] = useState(null);
  const [emailAlert, setEmailAlert] = useState(null);
  const { email } = getValues();

  const submitRef = useRef(null);

  useEffect(() => {
    if (otp.length === 6) {
      submitRef.current.focus();
    }
  }, [otp]);

  return (
    <div className="mb-10" id="accountInfo">
      {userData && (
        <>
          <div className="mb-10 mt-8">
            <form
              onSubmit={handleSubmit(() => {
                openEmailVerificationDialog('changeEmail');
                setIsEditEmail(false);
              })}
              onReset={() => {
                resetEmail();
                setIsEditEmail(false);
              }}>
              <div className="flex justify-between mb-6">
                <h2 className="font-Korolev-Bold text-xl whitespace-nowrap">Email Address</h2>
                <hr className="my-auto w-full align-middle mx-6 border-dotted border-l-0 border-b-0 border-r-0 " />
                <div>{renderEmailButtons()}</div>
              </div>
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
                aria-disabled={!isEditEmail}>
                <div>
                  <Controller
                    control={controlEmail}
                    name="email"
                    defaultValue={emailAddress}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                      <Input
                        disabled={!isEditEmail}
                        type="email"
                        placeholder="Email"
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        className="w-full"
                        error={error}
                      />
                    )}
                  />
                </div>
              </div>
              {isEditEmail && (
                <div className="flex flex-row mt-6 items-start text-sm">
                  <label className="font-Korolev-Light items-start">
                    <input
                      type="checkbox"
                      defaultChecked={false}
                      className="mr-2 accent-black mt-0.5"
                      {...register('updateMarketing')}
                    />
                    <p className="inline">
                      I confirm and consent to transferring my marketing{' '}
                      <button className="underline" onClick={() => handleTabChange(4, '')}>
                        communication preferences
                      </button>{' '}
                      from my previous email address to my new email address.
                    </p>
                  </label>
                </div>
              )}
            </form>
            {emailAlert && (
              <div className="mt-5">
                <Alert setShowAlert={setEmailAlert} loggedIn success={emailAlert.success}>
                  <p className="pr-12 text-sm md:text-base">{emailAlert.text}</p>
                </Alert>
              </div>
            )}
          </div>
          <div className="mb-10 mt-8">
            <form
              onSubmit={handleSubmit((data) => {
                onSubmit(data);
                setIsEdit(false);
              })}
              onReset={() => {
                setUserData();
                fetchUser();
                reset();
                setIsEdit(false);
              }}>
              <div className="flex justify-between mb-6">
                <h2 className="font-Korolev-Bold text-xl whitespace-nowrap">Account Details</h2>
                <hr className="my-auto w-full align-middle mx-6 border-dotted border-l-0 border-b-0 border-r-0 " />
                <div>{renderButtons()}</div>
              </div>
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
                aria-disabled={!isEdit}>
                <div>
                  <Controller
                    control={control}
                    name="username"
                    placeholder="username"
                    defaultValue={username}
                    rules={{
                      required: 'Username is required',
                      minLength: {
                        value: 3,
                        message: 'Username must be at least 3 characters long'
                      },
                      maxLength: {
                        value: 25,
                        message: 'Username must be at most 25 characters long'
                      },
                      validate: (value) =>
                        !startsAndEndsWithAlphaNumeric(value)
                          ? 'Username should start and end with alphanumeric'
                          : containsValidUsernameCharacters(value)
                          ? "Username can only contain alphanumeric, spaces and non-repeating special characters: , . ' -"
                          : true
                    }}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                      <Input
                        disabled={!isEdit}
                        label="USERNAME"
                        type="text"
                        placeholder="username"
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        className="w-full"
                        error={error}
                      />
                    )}
                  />
                </div>

                <div>
                  <Controller
                    defaultValue={firstName}
                    control={control}
                    name="firstName"
                    rules={{
                      validate: (value) =>
                        firstName === '' || value !== '' || 'First Name is required'
                    }}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                      <Input
                        disabled={!isEdit}
                        label="FIRST NAME"
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        className="w-full"
                        error={error}
                      />
                    )}
                  />
                </div>

                <div>
                  <Controller
                    control={control}
                    name="country"
                    defaultValue={countryOptions.find((c) => c.value === country)}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                      <SelectInput
                        label="COUNTRY / REGION"
                        disabled={!isEdit}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        options={countryOptions}
                        className={'w-full'}
                        placeholder={'Select Country / Region'}
                        error={error}
                        errorClassName="absolute"
                      />
                    )}
                  />
                </div>

                <div>
                  <Controller
                    defaultValue={langOptions.find((l) => l.value === language.substring(0, 2))}
                    control={control}
                    name="language"
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                      <SelectInput
                        label="LANGUAGE"
                        disabled={!isEdit}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        options={langOptions}
                        className={'w-full'}
                        placeholder={'Select Language'}
                        error={error}
                        errorClassName="absolute"
                      />
                    )}
                  />
                </div>

                <div>
                  <Controller
                    defaultValue={discordName}
                    control={control}
                    name="discordName"
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                      <Input
                        disabled={!isEdit}
                        label="DISCORD NAME"
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        className="w-full"
                        error={error}
                      />
                    )}
                  />
                </div>

                {parentalEmail && (
                  <div>
                    <Controller
                      control={control}
                      name="parentalEmail"
                      defaultValue={parentalEmail}
                      rules={{
                        required: {
                          value: true,
                          message: 'Parental/Guardian email is required'
                        },
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Please enter a valid parent/guardians email address'
                        }
                      }}
                      render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                        <Input
                          disabled={!isEdit}
                          label="PARENTAL EMAIL"
                          placeholder="Parent/Guardian Email"
                          type="parentalEmail"
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          className="w-full"
                          error={error}
                        />
                      )}
                    />
                  </div>
                )}
              </div>
            </form>
            {internalAlert && (
              <div className="mt-5">
                <Alert setShowAlert={setInternalAlert} loggedIn success={internalAlert.success}>
                  <p className="pr-12 text-sm md:text-base">{internalAlert.text}</p>
                </Alert>
              </div>
            )}
          </div>

          <hr className="w-full border-dotted border-l-0 border-b-0 border-r-0 mb-10" />

          <div className="flex flex-col justify-between mb-10">
            <h2 className="font-Korolev-Bold text-xl  mb-2">Personal Data Request</h2>
            <p className="mb-6">
              Request a copy of the personal information you have shared with Build A Rocket Boy.
            </p>

            <button
              onClick={() => openVerificationDialog('gdpr')}
              className="uppercase w-full sm:w-[250px] pt-4 pb-3 bg-black text-white">
              Request Data
            </button>
          </div>

          <hr className="w-full border-dotted border-l-0 border-b-0 border-r-0 mb-10" />
          <div className="flex flex-col justify-between mb-10">
            <h2 className="font-Korolev-Bold text-xl mb-2">Unlink Account</h2>
            <p>
              Please contact{' '}
              <a className="underline font-Korolev-Bold" href="mailto:support@buildarocketboy.com">
                support@buildarocketboy.com
              </a>{' '}
              to begin the process of unlinking a third-party account and BARB Account, entering
              &quot;Account Unlinking&quot; in the subject line. To help speed up your request,
              please provide the name of the platform you wish to unlink and the username and email
              associated with your account on that platform.
            </p>
          </div>
          <hr className="w-full border-dotted border-l-0 border-b-0 border-r-0 mb-10" />

          {userData &&
            (userData.daysToDeletion ? (
              <div className="flex flex-col justify-between mb-16">
                <h2 className="font-Korolev-Bold text-xl  mb-2">Delete Account</h2>
                <p className="mb-6">
                  Your account is scheduled to be deleted in {userData.daysToDeletion} days. Select
                  ”Remove Delete Request” to prevent your account from being deleted.
                </p>

                <button
                  onClick={() => removeDelete()}
                  className="uppercase w-full sm:w-[250px] pt-4 pb-3 bg-red text-white">
                  Remove Delete Request
                </button>
              </div>
            ) : (
              <>
                {userData.deletable ? (
                  <div className="flex flex-col justify-between mb-16">
                    <h2 className="font-Korolev-Bold text-xl  mb-2">Delete Account</h2>
                    <p className="mb-6">
                      Deleting your account is non recoverable. The process will take up to 14 days
                      to complete. During this 14 day period, you may remove your request at any
                      time. Click “Delete Account” to begin this process.
                    </p>

                    <button
                      onClick={() => openVerificationDialog('delete')}
                      className="uppercase w-full sm:w-[250px] pt-4 pb-3 bg-red text-white">
                      Delete Account
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col justify-between mb-16">
                    <h2 className="font-Korolev-Bold text-xl mb-2">Delete Account</h2>
                    <p className="mb-6">
                      Please contact{' '}
                      <a
                        className="underline font-Korolev-Bold"
                        href="mailto:privacy@buildarocketboy.com">
                        privacy@buildarocketboy.com
                      </a>{' '}
                      to begin your account delete request enter &quot;Account Delete Request&quot;
                      in the subject line and include your username in the email to assist our
                      support team with your process.
                    </p>
                  </div>
                )}
              </>
            ))}

          {deleteAlert && (
            <div className="mt-5">
              <Alert setShowAlert={setDeleteAlert} loggedIn success={deleteAlert.success}>
                <p className="pr-12 text-sm md:text-base">{deleteAlert.text}</p>
              </Alert>
            </div>
          )}
        </>
      )}

      <Dialog
        isOpen={verifying}
        onClose={() => {
          setVerifying(null);
          setOtp('');
        }}
        alert={alert}
        setAlert={setAlert}
        email={email}
        body={
          <div>
            <h1 className="font-Korolev-Bold text-5xl xl:text-5xl uppercase mb-6">
              Verification <br /> Required
            </h1>

            <img src={rocketBoyImg} alt="build rocket boy" className="w-full mb-8" />

            <p className="text-black font-Korolev-Medium text-base mb-2">
              Email Address Verification Required.
            </p>

            <p className="text-black font-Korolev-Light text-base mb-6">
              {verifying === 'gdpr'
                ? 'Enter the six digit code sent to your email address to request a copy of the personal information you have shared with Build A Rocket Boy. If you do not see the email in your inbox, please check your junk or spam mail folder.'
                : verifying === 'delete'
                ? 'Enter the six digit code sent to your email address to begin the delete process for your Build A Rocket Boy account.  If you do not see the email in your inbox, please check your junk or spam mail folder.'
                : 'Please enter the six digit verification code sent to your new email address to process your email address change request. Your code is valid for the next 20 minutes. '}
            </p>

            <form onSubmit={handleSubmitVerify(() => onVerificationSubmit(verifying))}>
              <div className="mb-8 otp gap-3">
                <OtpInput
                  numInputs={6}
                  value={otp}
                  onChange={setOtp}
                  shouldAutoFocus
                  inputType="tel"
                  renderInput={(props) => (
                    <input
                      {...props}
                      className="border border-gray-light !w-[45px] sm:!w-[55px] h-[60px] sm:h-[70px] text-black focus:border focus:border-black focus-visible:outline-none"
                    />
                  )}
                  onPaste={(event) => {
                    const pastedData = event.clipboardData.getData('text');
                    const numericData = pastedData.replace(/[^0-9]/g, '');
                    setOtp(numericData.slice(0, 6));
                  }}
                />
              </div>

              <hr className="w-full border-dotted border-l-0 border-b-0 border-r-0 mb-10 border-gray-light" />

              <ButtonAuth
                isSubmitting={isSubmittingVerify}
                isDisabled={!isValidVerify || otp?.length < 6}
                label={'submit'}
                submitRef={submitRef}
                className="mb-5"
              />
              <ButtonAuth
                type="button"
                onClick={() => {
                  verifying === 'changeEmail' ? getNewEmailCode(verifying) : getNewCode(verifying);
                }}
                label={'SEND NEW VERIFICATION CODE'}
                className="mb-5 "
              />
              <p className="text-black font-Korolev-Light text-base mb-14">
                Verification code valid for 20 minutes.
              </p>
            </form>
          </div>
        }
      />
    </div>
  );
};

export default AccountInfo;
